import type { HudLayout } from "./hud/slots.ts";
import { writeBarPaging, type BarPagingChoice, type SettingsStore } from "./settings.ts";

/**
 * A store laid out on the HUD's area, which the Ability bar choice reaches through the area's `barFive`.
 */
export interface LaidOut {
  getSnapshot(): { readonly layout: HudLayout };
  setLayout(layout: HudLayout): void;
}

/**
 * The page's Ability bar choice (P1.9-T133; ADR-061's note for P1.9-T133): All 10 or Five at a time, carried on the HUD's
 * area as `barFive`, so every surface that places itself by the bar (`barPaged`, `slotRect`) follows it. Made from what
 * the store holds as the page loads, it puts the choice on each store's area at once and takes each change Settings
 * makes, which applies at once and is kept per device. Where the area is too narrow for ten the bar pages whatever the
 * choice (`barMustPage`).
 *
 * @example
 * ```ts
 * const barPaging = new BarPagingSetting([hud, chatStore], readBarPaging());
 * hud.setLayout(barPaging.laidOut(hudLayout(view)));
 * barPaging.setChoice("five");
 * ```
 */
export class BarPagingSetting {
  /**
   * @param stores The stores laid out on the HUD's area: the HUD's and the chat's.
   * @param current The choice as the page loads, as `readBarPaging` gives it.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    private readonly stores: readonly LaidOut[],
    private current: BarPagingChoice,
    private readonly store?: SettingsStore | null,
  ) {
    this.apply();
  }

  /**
   * The choice in force.
   *
   * @returns All 10 or Five at a time.
   */
  get choice(): BarPagingChoice {
    return this.current;
  }

  /**
   * An area with the choice on it, for a store's layout as the window changes.
   *
   * @param layout The area and the UI scale, as `hudLayout` gives them.
   * @returns The same area with `barFive` true for Five at a time and false for All 10.
   */
  laidOut(layout: HudLayout): HudLayout {
    return { ...layout, barFive: this.current === "five" };
  }

  /**
   * Takes the viewer's choice, which the bar and everything placed by it follow at once, and keeps it.
   *
   * @param choice All 10 or Five at a time.
   */
  setChoice(choice: BarPagingChoice): void {
    this.current = choice;
    this.apply();
    writeBarPaging(choice, this.store);
  }

  /**
   * Puts the choice on every store's area.
   */
  private apply(): void {
    for (const laid of this.stores) {
      laid.setLayout(this.laidOut(laid.getSnapshot().layout));
    }
  }
}
